// Table party subdivision — shared pure helpers (table-party-subdivision, Component 10).
//
// Mirrors admin-mobile-app/src/utils/partyLabel.js. Keep the two byte-identical apart
// from the one header line naming the mirror direction, following the existing
// money.js / menuName.js / addableOrder.js duplication convention.
//
// WHY THIS FILE EXISTS SEPARATELY. Requirement 7.5 demands that one order record print
// the same party text on the web and on mobile, and the two `buildPosOrderReceiptHtml`
// implementations are independent copies in two 5,000-line files. A shared pure
// resolver, tested in both packages, is the only way to hold that. Every party string
// that reaches a screen or a printer comes from here.
//
// Openness is NOT re-derived here. `isOpenTicket` composes `isAddableOrder` from
// addableOrder.js, which is already the one agreed expression of "this ticket is still
// live" and is itself mirrored from the backend. A second, subtly different notion of
// open would put the queue's banner and Take Order's pre-selection out of step, and
// the resulting bug ("it says one open ticket but the queue shows two") would be
// invisible until a cashier hit it.

import { isAddableOrder, orderField } from './addableOrder';

/**
 * Read a field from the order, falling back to its `customerSubmittedSnapshot` mirror,
 * treating BLANK as absent.
 *
 * WHY NOT `orderField` FROM addableOrder.js. That helper falls back only when the root
 * value is `null`/`undefined`, so a root of `''` short-circuits and the mirror is never
 * consulted. Every read of these fields elsewhere in the queue and the print builders is
 * written `order.X || order.customerSubmittedSnapshot?.X`, which DOES fall through on an
 * empty string. Records with `tableLabel: ""` exist (a to-go order, and the handler's own
 * test fixtures), so the two rules disagree on real data -- and the disagreement shows up
 * as a field that saved correctly and then renders blank, which is the exact failure the
 * snapshot mirror exists to prevent.
 *
 * `orderField` is left alone deliberately: it is a pre-existing shared helper with other
 * callers, and widening its contract to satisfy this feature would change their behaviour
 * too. This resolver adopts the `||` semantics that its own consumers already use.
 */
function field(order, key) {
  const own = orderField(order, key);
  if (own != null && String(own).trim() !== '') return own;
  const snap = order && typeof order === 'object' ? order.customerSubmittedSnapshot : null;
  if (snap && typeof snap === 'object' && snap[key] != null) return snap[key];
  return undefined;
}

/**
 * The offered party sequence, in the order it is offered.
 *
 * LATIN, DELIBERATELY, in every language (R9.2). Same reasoning that kept `GST` in
 * Latin for take-order-i18n: this identifier is read off a physical chit by a server,
 * a cook and a cashier, printed beside Arabic-numeral table numbers. One script is
 * safer than two for a string whose whole job is to be unambiguous out loud.
 *
 * Four is not a limit on parties, it is the number worth offering as chips. A table
 * seating five unrelated groups is not a case this feature is shaped for, and the
 * server can still type past the chips if the UI ever allows it.
 */
export const PARTY_LABELS = ['A', 'B', 'C', 'D'];

/**
 * normalizeTableLabel(raw) -> string
 *
 * FOR COMPARISON ONLY. Never persist this and never render it (R4.8, design Property
 * 6). `tableLabel` is free text, so "Patio 3", "patio 3" and " Patio 3 " are one table
 * to a human and must group as one; but rewriting what the server typed onto the chit
 * would be a visible defect in exchange for an invisible lookup.
 */
export function normalizeTableLabel(raw) {
  return String(raw == null ? '' : raw).trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * sameTable(a, b) -> boolean
 *
 * Normalized equality. Two blank labels are NOT the same table: a to-go ticket has no
 * table, and treating every table-less ticket as sharing one imaginary table would
 * make the multi-ticket banner fire across the entire to-go queue.
 */
export function sameTable(a, b) {
  const left = normalizeTableLabel(a);
  const right = normalizeTableLabel(b);
  return Boolean(left) && left === right;
}

/**
 * isOpenTicket(order) -> boolean
 *
 * A DINE-IN staff order that is not cancelled, not completed and not yet paid.
 *
 * Composed from `isAddableOrder` rather than reimplemented — see the file header. The
 * dine-in narrowing is the only thing added: a to-go order can be addable but can
 * never be an open ticket at a table, because it is not at one.
 */
export function isOpenTicket(order) {
  if (!isAddableOrder(order)) return false;
  return String(field(order, 'orderType') || '').trim().toUpperCase() === 'DINE_IN';
}

/**
 * openTicketsAtTable(orders, tableLabel) -> array
 *
 * The open tickets seated at one table, input order preserved. Returns [] for a blank
 * table so a caller mid-typing gets nothing rather than everything.
 */
export function openTicketsAtTable(orders, tableLabel) {
  if (!Array.isArray(orders)) return [];
  if (!normalizeTableLabel(tableLabel)) return [];
  return orders.filter(
    (o) => isOpenTicket(o) && sameTable(field(o, 'tableLabel'), tableLabel),
  );
}

/**
 * dineInTicketsAtTable(tickets, tableLabel) -> array
 *
 * The same question as `openTicketsAtTable`, for a list that has ALREADY had openness
 * applied server-side — specifically the `GET /admin/pos/orders/open-tickets` response,
 * whose handler filters with `is_addable_order` before projecting.
 *
 * READ THIS BEFORE CHOOSING BETWEEN THE TWO. That endpoint's projection is deliberately
 * narrow and omits `orderSource`, `payment_status` and `status` — the very fields
 * `isOpenTicket` inspects. Passing its entries to `openTicketsAtTable` therefore returns
 * an EMPTY list for every table, silently: the pre-selection would always say "A" and
 * the open-ticket count would always say zero, on a screen where both look plausible.
 *
 * So: `openTicketsAtTable` for full order records (the POS queue), this one for the
 * narrow projection (Take Order). Re-deriving openness here would not be stricter, it
 * would be wrong.
 */
export function dineInTicketsAtTable(tickets, tableLabel) {
  if (!Array.isArray(tickets)) return [];
  if (!normalizeTableLabel(tableLabel)) return [];
  return tickets.filter((tk) => {
    if (!tk || typeof tk !== 'object') return false;
    if (String(tk.orderType || '').trim().toUpperCase() !== 'DINE_IN') return false;
    return sameTable(tk.tableLabel, tableLabel);
  });
}

/**
 * nextPartyLabelFromTickets(tickets, tableLabel) -> string
 *
 * Pre-selection for Take Order, driven by the narrow open-tickets projection.
 */
export function nextPartyLabelFromTickets(tickets, tableLabel) {
  return nextPartyLabel(usedPartyLabels(dineInTicketsAtTable(tickets, tableLabel)));
}

/**
 * usedPartyLabels(orders) -> array of uppercase labels
 *
 * Which labels the given tickets already occupy. Deduplicated, and blanks dropped: a
 * ticket with no party does not consume a letter, which is what lets a table hold one
 * unlabelled ticket and still offer `A`.
 */
export function usedPartyLabels(orders) {
  if (!Array.isArray(orders)) return [];
  const seen = [];
  orders.forEach((o) => {
    const label = String(field(o, 'partyLabel') || '').trim().toUpperCase();
    if (label && !seen.includes(label)) seen.push(label);
  });
  return seen;
}

/**
 * nextPartyLabel(usedLabels) -> string
 *
 * The lowest label in PARTY_LABELS that nothing is using (R4.3), or the first label
 * when nothing is used (R4.4).
 *
 * LOWEST UNUSED, not next-after-highest. A table whose party B has paid and left
 * should reuse B for the group that sits down next, because the letter is a floor-level
 * convenience scoped to concurrently-open tickets, not an identity (R10.1, R10.2).
 *
 * Falls back to the last label when every label is taken. Returning '' would leave the
 * server with no pre-selection in the one case where the table is most confusing; a
 * duplicate that the collision warning then flags is the better failure.
 */
export function nextPartyLabel(usedLabels) {
  const used = (Array.isArray(usedLabels) ? usedLabels : []).map((l) =>
    String(l == null ? '' : l).trim().toUpperCase(),
  );
  const free = PARTY_LABELS.find((label) => !used.includes(label));
  return free || PARTY_LABELS[PARTY_LABELS.length - 1];
}

/**
 * nextPartyLabelAtTable(orders, tableLabel) -> string
 *
 * The convenience composition Take Order actually calls: look at the open tickets for
 * this table, and pre-select the lowest label none of them is using.
 */
export function nextPartyLabelAtTable(orders, tableLabel) {
  return nextPartyLabel(usedPartyLabels(openTicketsAtTable(orders, tableLabel)));
}

/**
 * partyLabelOf(order) / partyNameOf(order) -> string
 *
 * Read either field from the order or its `customerSubmittedSnapshot` mirror. Both
 * places are legitimate: order creation mirrors them so the queue and print builders
 * can read them without a schema migration (R5.7).
 */
export function partyLabelOf(order) {
  return String(field(order, 'partyLabel') || '').trim();
}

export function partyNameOf(order) {
  return String(field(order, 'partyName') || '').trim();
}

/**
 * partyDisplayText(order) -> string
 *
 * The party fragment for a badge, a chit or a bill: `A`, or `A · Kumar`. Empty string
 * when the order has no party, so every caller can concatenate unconditionally and
 * still emit byte-identical output for an order without one (R7.4).
 *
 * A name with no letter still renders (R5.5) — "Kumar" is useful on a chit even when
 * nobody picked a letter.
 */
export function partyDisplayText(order) {
  const label = partyLabelOf(order);
  const name = partyNameOf(order);
  if (label && name) return `${label} · ${name}`;
  return label || name || '';
}

/**
 * ticketPartyText(order) -> string
 *
 * Table and party together, as a human says it: `Table 12 · B · Kumar`. Empty when
 * there is nothing to say.
 *
 * This is the ONE function the kitchen chit, the guest bill and the queue badge all
 * resolve their party text through, on both surfaces. That is what makes Property 11
 * (cross-surface print parity) hold by construction rather than by vigilance.
 */
export function ticketPartyText(order) {
  const table = String(field(order, 'tableLabel') || '').trim();
  const party = partyDisplayText(order);
  if (table && party) return `Table ${table} · ${party}`;
  if (table) return `Table ${table}`;
  return party;
}

/**
 * groupOpenTicketsByTable(orders) -> Map of normalized table -> count
 *
 * How many open tickets each table has. Drives both the multi-ticket banner and the
 * settlement confirmation trigger, computed once so the two cannot disagree about
 * whether a table is ambiguous.
 */
export function openTicketCountsByTable(orders) {
  const counts = new Map();
  if (!Array.isArray(orders)) return counts;
  orders.forEach((o) => {
    if (!isOpenTicket(o)) return;
    const key = normalizeTableLabel(field(o, 'tableLabel'));
    if (!key) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
}

/**
 * hasSharedTable(order, counts) -> boolean
 *
 * Whether this order's table holds two or more open tickets — the condition that makes
 * a ticket ambiguous, and therefore the condition the queue banner and the settle
 * confirmation both key on.
 */
export function hasSharedTable(order, counts) {
  const key = normalizeTableLabel(field(order, 'tableLabel'));
  if (!key || !counts || typeof counts.get !== 'function') return false;
  return (counts.get(key) || 0) >= 2;
}

/**
 * tablePartiesEnabled(settings) -> boolean
 *
 * The gate, resolved from a settings object or a queue response (R1.10, R1.11).
 *
 * FAIL-CLOSED, and that is the whole contract. Absent block, null, malformed, string
 * "false", or settings that have not loaded yet all resolve to false. Only an explicit
 * boolean `true` opts a tenant in.
 *
 * The direction matters: resolving a not-yet-loaded gate to true would flash the party
 * affordances at tenants that never asked for them, on every queue load.
 */
export function tablePartiesEnabled(settings) {
  if (!settings || typeof settings !== 'object') return false;
  const block = settings.tableParties;
  if (!block || typeof block !== 'object') return false;
  return block.enabled === true;
}
